export type TimeEntryContextOption = {
    id: string;
    label: string;
    projectId?: string;
    projectName?: string;
    clientId?: string;
    clientName?: string;
};

export function useTimeEntryContextOptions() {
    const taskStore = useTaskStore();
    const projectStore = useProjectStore();
    const clientStore = useClientStore();

    const projectMap = computed(() => new Map(projectStore.projects.map(project => [project.id, project])));
    const clientMap = computed(() => new Map(clientStore.clients.map(client => [client.id, client])));

    const taskOptions = computed<TimeEntryContextOption[]>(() => taskStore.tasks.map(task => {
        const project = task.projectId ? projectMap.value.get(task.projectId) : null;
        const clientId = task.clientId || project?.clientId;
        const client = clientId ? clientMap.value.get(clientId) : null;
        return {
            id: task.id,
            label: task.title || 'Untitled task',
            projectId: project?.id,
            projectName: project?.name,
            clientId: client?.id,
            clientName: client?.name,
        };
    }));

    const projectOptions = computed<TimeEntryContextOption[]>(() => projectStore.projects.map(project => ({
        id: project.id,
        label: project.name,
        clientId: project.clientId,
        clientName: project.clientId ? clientMap.value.get(project.clientId)?.name : undefined,
    })));

    const clientOptions = computed<TimeEntryContextOption[]>(() => clientStore.clients.map(client => ({ id: client.id, label: client.name })));

    async function ensureTimeEntryContextOptions() {
        await Promise.all([
            taskStore.tasks.length ? Promise.resolve() : taskStore.fetchTasks(),
            projectStore.projects.length ? Promise.resolve() : projectStore.fetchProjects(),
            clientStore.clients.length ? Promise.resolve() : clientStore.fetchClients(),
        ]);
    }

    return {
        taskOptions,
        projectOptions,
        clientOptions,
        ensureTimeEntryContextOptions,
    };
}
